import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Typography,
    useTheme,
} from '@mui/material'
import { History, Receipt } from '@mui/icons-material'
import { Bill } from '../../types/bills'
import { BillingHistoryRow } from './BillingHistoryRow'

type HistoryFilter = 'all' | 'unpaid' | 'paid' | 'overdue'

const FILTERS: { id: HistoryFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'unpaid', label: 'Unpaid' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'paid', label: 'Paid' },
]

const matchesFilter = (bill: Bill, filter: HistoryFilter) => {
    if (filter === 'paid') {
        return bill.is_paid || bill.balance_due <= 0
    }
    if (filter === 'overdue') {
        return bill.is_overdue
    }
    if (filter === 'unpaid') {
        return !bill.is_paid && bill.balance_due > 0
    }
    return true
}

const sortBills = (bills: Bill[]) =>
    [...bills].sort((a, b) => {
        const monthDiff =
            new Date(b.billing_month).getTime() - new Date(a.billing_month).getTime()
        if (monthDiff !== 0) {
            return monthDiff
        }
        return new Date(b.due_date).getTime() - new Date(a.due_date).getTime()
    })

export const BillingHistory = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const [selectedFilter, setSelectedFilter] = useState<HistoryFilter>('all')
    const { bills, loading, error } = useSelector((state: RootState) => state.billing)

    const sortedBills = useMemo(() => sortBills(bills || []), [bills])
    const filteredBills = useMemo(
        () => sortedBills.filter((bill) => matchesFilter(bill, selectedFilter)),
        [sortedBills, selectedFilter],
    )
    const counts = useMemo(
        () => ({
            all: sortedBills.length,
            unpaid: sortedBills.filter((bill) => matchesFilter(bill, 'unpaid')).length,
            overdue: sortedBills.filter((bill) => matchesFilter(bill, 'overdue')).length,
            paid: sortedBills.filter((bill) => matchesFilter(bill, 'paid')).length,
        }),
        [sortedBills],
    )

    const handlePayClick = (bill: Bill) => {
        navigate(`/bill-pay?billId=${bill.id}`)
    }

    return (
        <Card
            sx={{
                width: '100%',
                maxWidth: '800px',
                borderRadius: '16px',
                bgcolor: '#2a2f35',
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
            }}
        >
            <CardHeader
                title={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <History
                                sx={{
                                    mr: 1,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{ color: 'rgba(255,255,255,0.85)' }}
                            >
                                Bill History
                            </Typography>
                        </Box>
                        {sortedBills.length > 0 && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.42)' }}
                            >
                                {sortedBills.length}{' '}
                                {sortedBills.length === 1 ? 'bill' : 'bills'}
                            </Typography>
                        )}
                    </Box>
                }
            />
            <CardContent sx={{ pt: 0 }}>
                {sortedBills.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {FILTERS.map((filter) => {
                            const selected = selectedFilter === filter.id
                            return (
                                <Chip
                                    key={filter.id}
                                    label={`${filter.label} ${counts[filter.id]}`}
                                    onClick={() => setSelectedFilter(filter.id)}
                                    sx={{
                                        height: 28,
                                        borderRadius: '999px',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        backgroundColor: selected
                                            ? theme.palette.primary.main
                                            : 'rgba(255,255,255,0.06)',
                                        color: selected
                                            ? '#fff'
                                            : 'rgba(255,255,255,0.72)',
                                        border: selected
                                            ? 'none'
                                            : '1px solid rgba(255,255,255,0.08)',
                                        '&:hover': {
                                            backgroundColor: selected
                                                ? theme.palette.primary.dark
                                                : 'rgba(255,255,255,0.1)',
                                        },
                                    }}
                                />
                            )
                        })}
                    </Box>
                )}

                {loading && !bills ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={36} sx={{ color: theme.palette.primary.main }} />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 5,
                            color: 'rgba(255,255,255,0.55)',
                        }}
                    >
                        <Typography>{error}</Typography>
                    </Box>
                ) : filteredBills.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 5,
                            px: 2,
                            border: '1px dashed rgba(255,255,255,0.12)',
                            borderRadius: 2,
                        }}
                    >
                        <Receipt
                            sx={{ fontSize: 40, color: 'rgba(255,255,255,0.28)', mb: 1 }}
                        />
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                            {sortedBills.length === 0
                                ? 'No bills yet'
                                : `No ${selectedFilter} bills`}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5 }}
                        >
                            {sortedBills.length === 0
                                ? 'Your monthly bills will show up here.'
                                : 'Try a different filter to see more bills.'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {filteredBills.map((bill) => (
                            <BillingHistoryRow
                                key={bill.id}
                                bill={bill}
                                onPayClick={handlePayClick}
                            />
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}
